import { useEffect, useState, useCallback } from "react";
import { Typography, Spin, Modal } from "antd";
import {
    EditOutlined,
    ExclamationCircleOutlined,
    UserAddOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    CopyOutlined,
    ReloadOutlined,
    CheckOutlined,
} from "@ant-design/icons";
import ApiClient from "../api";

// ─────────────────────────────────────────────────────────────────────────────
// Constants & helpers
// ─────────────────────────────────────────────────────────────────────────────

const api_client = new ApiClient();
const { Text }   = Typography;

const roleColors = {
    AUDIDOX: { bg: 'rgba(121,167,165,0.15)', text: '#79A7A5', border: 'rgba(121,167,165,0.3)' },
    ADMIN:   { bg: 'rgba(250,189,88,0.12)',  text: '#FABB44', border: 'rgba(250,189,88,0.25)' },
    USER:    { bg: 'rgba(148,163,184,0.1)',  text: '#94A3B8', border: 'rgba(148,163,184,0.2)' },
};

const getRoleStyle = (role) => roleColors[role] || roleColors.USER;
const getInitials  = (name = '', surname = '') =>
    `${name.charAt(0)}${surname.charAt(0)}`.toUpperCase();

// ─────────────────────────────────────────────────────────────────────────────
// Cognito-compliant password generator
// Policy: ≥8 chars, uppercase, lowercase, digit, special char
// ─────────────────────────────────────────────────────────────────────────────

const UPPER   = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWER   = 'abcdefghijklmnopqrstuvwxyz';
const DIGITS  = '0123456789';
const SPECIAL = '!@#$%^&*()_+-=[]{}|;:,.<>?';
const ALL     = UPPER + LOWER + DIGITS + SPECIAL;

const generatePassword = (length = 16) => {
    const rand     = (str) => str[Math.floor(Math.random() * str.length)];
    const required = [rand(UPPER), rand(LOWER), rand(DIGITS), rand(SPECIAL)];
    const rest     = Array.from({ length: length - 4 }, () => rand(ALL));
    return [...required, ...rest].sort(() => Math.random() - 0.5).join('');
};

const strengthScore = (pwd) => {
    let s = 0;
    if (pwd.length >= 12)         s++;
    if (/[A-Z]/.test(pwd))        s++;
    if (/[0-9]/.test(pwd))        s++;
    if (/[^a-zA-Z0-9]/.test(pwd)) s++;
    return s;
};

const STRENGTH_META = [
    { label: 'Weak',   color: '#EF4444' },
    { label: 'Fair',   color: '#FABB44' },
    { label: 'Good',   color: '#FABB44' },
    { label: 'Strong', color: '#4ADE80' },
    { label: 'Strong', color: '#4ADE80' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Shared styles
// ─────────────────────────────────────────────────────────────────────────────

const inputStyle = {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid var(--border-default)',
    background: 'var(--bg-base)',
    color: 'var(--text-primary)',
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
};

const labelStyle = {
    display: 'block',
    color: 'var(--text-secondary, #94A3B8)',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    marginBottom: 5,
};

const btnBase = {
    padding: '8px 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    transition: 'opacity 0.15s',
};

// ─────────────────────────────────────────────────────────────────────────────
// Toast
// ─────────────────────────────────────────────────────────────────────────────

const Toast = ({ toasts }) => (
    <div style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: 10,
        pointerEvents: 'none',
    }}>
        {toasts.map(t => (
            <div key={t.id} style={{
                padding: '12px 18px',
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 500,
                background: t.type === 'error' ? 'rgba(239,68,68,0.12)' : 'rgba(74,222,128,0.1)',
                border: `1px solid ${t.type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(74,222,128,0.3)'}`,
                color: t.type === 'error' ? '#EF4444' : '#4ADE80',
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                animation: 'fadeSlideIn 0.25s ease',
                maxWidth: 320,
            }}>
                {t.message}
            </div>
        ))}
    </div>
);

const useToast = () => {
    const [toasts, setToasts] = useState([]);
    const push = useCallback((message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
    }, []);
    return { toasts, push };
};

// ─────────────────────────────────────────────────────────────────────────────
// Field — defined outside modals to prevent remount on each keystroke
// ─────────────────────────────────────────────────────────────────────────────

const Field = ({ label, fieldKey, type = 'text', placeholder, value, error, onChange }) => (
    <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>{label}</label>
        <input
            type={type}
            value={value}
            onChange={e => onChange(fieldKey, e.target.value)}
            placeholder={placeholder}
            style={{
                ...inputStyle,
                borderColor: error ? '#EF4444' : 'var(--border-default)',
            }}
            onFocus={e => { if (!error) e.target.style.borderColor = '#79A7A5'; }}
            onBlur={e  => { if (!error) e.target.style.borderColor = 'var(--border-default)'; }}
        />
        {error && (
            <span style={{ color: '#EF4444', fontSize: 11, marginTop: 4, display: 'block' }}>
                {error}
            </span>
        )}
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Strength Bar
// ─────────────────────────────────────────────────────────────────────────────

const StrengthBar = ({ password }) => {
    const score = strengthScore(password);
    const meta  = STRENGTH_META[score];
    return (
        <div style={{ marginTop: 6, marginBottom: 4 }}>
            <div style={{ display: 'flex', gap: 4 }}>
                {[0, 1, 2, 3].map(i => (
                    <div key={i} style={{
                        flex: 1, height: 3, borderRadius: 2,
                        background: i < score ? meta.color : 'var(--border-default)',
                        transition: 'background 0.3s',
                    }} />
                ))}
            </div>
            {password && (
                <span style={{ color: meta.color, fontSize: 10, fontWeight: 600, marginTop: 3, display: 'block' }}>
                    {meta.label}
                </span>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Icon Button
// ─────────────────────────────────────────────────────────────────────────────

const IconBtn = ({ children, onClick, title, active }) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        style={{
            width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer',
            background: active ? 'rgba(121,167,165,0.2)' : 'transparent',
            color: active ? '#79A7A5' : 'var(--text-secondary, #94A3B8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, transition: 'all 0.15s',
        }}
        onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(121,167,165,0.15)';
            e.currentTarget.style.color = '#79A7A5';
        }}
        onMouseLeave={e => {
            e.currentTarget.style.background = active ? 'rgba(121,167,165,0.2)' : 'transparent';
            e.currentTarget.style.color = active ? '#79A7A5' : 'var(--text-secondary, #94A3B8)';
        }}
    >
        {children}
    </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// Password Field
// ─────────────────────────────────────────────────────────────────────────────

const PasswordField = ({ value, onRegenerate }) => {
    const [visible, setVisible] = useState(true);
    const [copied,  setCopied]  = useState(false);

    const copy = () => {
        navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                <label style={labelStyle}>Generated Password</label>
                <span style={{ fontSize: 10, color: 'var(--text-secondary, #94A3B8)', fontStyle: 'italic' }}>
                    Cognito-compliant · system generated
                </span>
            </div>

            <div style={{
                display: 'flex', alignItems: 'center',
                border: '1px solid var(--border-default)', borderRadius: 8,
                background: 'var(--bg-base)', overflow: 'hidden',
                transition: 'border-color 0.15s',
            }}
                 onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(121,167,165,0.4)'}
                 onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
            >
                <input
                    readOnly
                    type={visible ? 'text' : 'password'}
                    value={value}
                    style={{
                        flex: 1,
                        padding: '9px 12px',
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--text-primary)',
                        fontSize: 13,
                        fontFamily: 'monospace',
                        letterSpacing: visible ? '0.05em' : '0.18em',
                        outline: 'none',
                        cursor: 'default',
                    }}
                />
                <div style={{ display: 'flex', gap: 2, paddingRight: 6, flexShrink: 0 }}>
                    <IconBtn
                        title={visible ? 'Hide password' : 'Show password'}
                        onClick={() => setVisible(v => !v)}
                    >
                        {visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    </IconBtn>
                    <IconBtn
                        title={copied ? 'Copied!' : 'Copy to clipboard'}
                        onClick={copy}
                        active={copied}
                    >
                        {copied ? <CheckOutlined /> : <CopyOutlined />}
                    </IconBtn>
                    <IconBtn title="Generate new password" onClick={onRegenerate}>
                        <ReloadOutlined />
                    </IconBtn>
                </div>
            </div>

            <StrengthBar password={value} />

            <span style={{ fontSize: 10, color: 'var(--text-secondary, #94A3B8)', display: 'block' }}>
                Share this securely with the user — they can change it after first login.
            </span>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Add User Modal
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_FORM = { user_name: '', user_surname: '', user_email: '' };

const AddUserModal = ({ open, loading, onConfirm, onCancel }) => {
    const [form,     setForm]     = useState(EMPTY_FORM);
    const [errors,   setErrors]   = useState({});
    const [password, setPassword] = useState('');

    useEffect(() => {
        if (open) {
            setForm(EMPTY_FORM);
            setErrors({});
            setPassword(generatePassword());
        }
    }, [open]);

    const set = useCallback((key, val) => {
        setForm(prev => ({ ...prev, [key]: val }));
        setErrors(prev => ({ ...prev, [key]: '' }));
    }, []);

    const validate = () => {
        const e = {};
        if (!form.user_name.trim())    e.user_name    = 'First name is required';
        if (!form.user_surname.trim()) e.user_surname = 'Surname is required';
        if (!form.user_email.trim())   e.user_email   = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.user_email))
            e.user_email = 'Enter a valid email address';
        return e;
    };

    const handleSubmit = () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        onConfirm({ ...form, password });
    };

    return (
        <Modal
            open={open}
            footer={null}
            closable={false}
            centered
            width={480}
            styles={{
                content: {
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 16,
                    padding: 0,
                    boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
                },
            }}
        >
            <div style={{ padding: '28px 28px 24px' }}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                    <div style={{
                        width: 42, height: 42, borderRadius: 11,
                        background: 'rgba(121,167,165,0.12)', border: '1px solid rgba(121,167,165,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <UserAddOutlined style={{ color: '#79A7A5', fontSize: 18 }} />
                    </div>
                    <div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 16 }}>
                            Add New User
                        </div>
                        <div style={{ color: 'var(--text-secondary, #94A3B8)', fontSize: 12 }}>
                            Role will be set to Admin
                        </div>
                    </div>
                </div>

                {/* Name row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <Field
                        label="First Name"
                        fieldKey="user_name"
                        placeholder="e.g. John"
                        value={form.user_name}
                        error={errors.user_name}
                        onChange={set}
                    />
                    <Field
                        label="Surname"
                        fieldKey="user_surname"
                        placeholder="e.g. Smith"
                        value={form.user_surname}
                        error={errors.user_surname}
                        onChange={set}
                    />
                </div>

                <Field
                    label="Email Address"
                    fieldKey="user_email"
                    type="email"
                    placeholder="user@company.com"
                    value={form.user_email}
                    error={errors.user_email}
                    onChange={set}
                />

                <PasswordField
                    value={password}
                    onRegenerate={() => setPassword(generatePassword())}
                />

                <div style={{ height: 1, background: 'var(--border-default)', margin: '4px 0 20px' }} />

                {/* Footer */}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button
                        onClick={onCancel}
                        disabled={loading}
                        style={{
                            ...btnBase,
                            border: '1px solid var(--border-default)',
                            background: 'transparent',
                            color: 'var(--text-primary)',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        style={{
                            ...btnBase,
                            background: '#79A7A5',
                            color: '#0D0F14',
                            fontWeight: 700,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1,
                            display: 'flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        {loading && <Spin size="small" style={{ filter: 'brightness(0)' }} />}
                        Add User
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Edit Modal — activate / deactivate only
// updaterole(role_id, active) is the only operation
// ─────────────────────────────────────────────────────────────────────────────

const EditModal = ({ open, user, loading, onConfirm, onCancel }) => {
    if (!user) return null;

    const willActivate = !user.active;
    const color  = willActivate ? '#4ADE80'               : '#FABB44';
    const bg     = willActivate ? 'rgba(74,222,128,0.08)' : 'rgba(250,189,88,0.08)';
    const border = willActivate ? 'rgba(74,222,128,0.2)'  : 'rgba(250,189,88,0.2)';
    const action = willActivate ? 'Activate'              : 'Deactivate';
    const title  = willActivate ? 'Activate User'         : 'Deactivate User';
    const msg    = willActivate
        ? `This will restore system access for ${user.user_name} ${user.user_surname}.`
        : `This will revoke system access for ${user.user_name} ${user.user_surname}.`;

    return (
        <Modal
            open={open}
            footer={null}
            closable={false}
            centered
            width={420}
            styles={{
                content: {
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 16,
                    padding: 0,
                    boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
                },
            }}
        >
            <div style={{ padding: '28px 28px 24px' }}>

                {/* Title row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div style={{
                        width: 42, height: 42, borderRadius: 11,
                        background: bg, border: `1px solid ${border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <ExclamationCircleOutlined style={{ color, fontSize: 18 }} />
                    </div>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 16 }}>
                        {title}
                    </span>
                </div>

                {/* User pill with current status */}
                <div style={{
                    background: 'var(--bg-base)', border: '1px solid var(--border-default)',
                    borderRadius: 10, padding: '10px 14px', marginBottom: 14,
                    display: 'flex', alignItems: 'center', gap: 10,
                }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #79A7A5 0%, #4d8280 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, color: '#0D0F14', flexShrink: 0,
                    }}>
                        {getInitials(user.user_name, user.user_surname)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 13 }}>
                            {user.user_name} {user.user_surname}
                        </div>
                        <div style={{ color: 'var(--text-secondary, #94A3B8)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {user.user_email}
                        </div>
                    </div>
                    {/* Current status badge */}
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, flexShrink: 0,
                        background: user.active ? 'rgba(74,222,128,0.1)' : 'rgba(239,68,68,0.1)',
                        color:      user.active ? '#4ADE80' : '#EF4444',
                        border:     `1px solid ${user.active ? 'rgba(74,222,128,0.25)' : 'rgba(239,68,68,0.2)'}`,
                    }}>
                        <span style={{
                            width: 5, height: 5, borderRadius: '50%',
                            background: user.active ? '#4ADE80' : '#EF4444',
                            display: 'inline-block',
                        }} />
                        {user.active ? 'Active' : 'Inactive'}
                    </span>
                </div>

                <p style={{ color: 'var(--text-secondary, #94A3B8)', fontSize: 13, margin: '0 0 24px', lineHeight: 1.6 }}>
                    {msg}
                </p>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button
                        onClick={onCancel}
                        disabled={loading}
                        style={{
                            ...btnBase,
                            border: '1px solid var(--border-default)',
                            background: 'transparent',
                            color: 'var(--text-primary)',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        style={{
                            ...btnBase,
                            background: color,
                            color: '#0D0F14',
                            fontWeight: 700,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1,
                            display: 'flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        {loading && <Spin size="small" style={{ filter: 'brightness(0)' }} />}
                        {action}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// User Card
// ─────────────────────────────────────────────────────────────────────────────

const UserCard = ({ user, index, onEdit }) => {
    const roleStyle = getRoleStyle(user.user_role);

    return (
        <div
            style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-default)',
                borderRadius: 12,
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                transition: 'border-color 0.2s, box-shadow 0.2s',
                animation: 'fadeSlideIn 0.3s ease both',
                animationDelay: `${index * 0.04}s`,
                opacity: user.active ? 1 : 0.6,
            }}
            onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(121,167,165,0.5)';
                e.currentTarget.style.boxShadow  = '0 4px 20px rgba(0,0,0,0.12)';
            }}
            onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border-default)';
                e.currentTarget.style.boxShadow  = 'none';
            }}
        >
            {/* Avatar */}
            <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: 'linear-gradient(135deg, #79A7A5 0%, #4d8280 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, fontSize: 14, fontWeight: 700, color: '#0D0F14',
            }}>
                {getInitials(user.user_name, user.user_surname)}
            </div>

            {/* Name + email */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                    <span style={{
                        color: 'var(--text-primary)', fontWeight: 600, fontSize: 14,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                        {user.user_name} {user.user_surname}
                    </span>

                    {/* Active badge */}
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, flexShrink: 0,
                        background: user.active ? 'rgba(74,222,128,0.1)' : 'rgba(239,68,68,0.1)',
                        color:      user.active ? '#4ADE80' : '#EF4444',
                        border:     `1px solid ${user.active ? 'rgba(74,222,128,0.25)' : 'rgba(239,68,68,0.2)'}`,
                    }}>
                        <span style={{
                            width: 5, height: 5, borderRadius: '50%', display: 'inline-block',
                            background: user.active ? '#4ADE80' : '#EF4444',
                        }} />
                        {user.active ? 'Active' : 'Inactive'}
                    </span>
                </div>
                <span style={{
                    color: 'var(--text-secondary, #94A3B8)', fontSize: 12, display: 'block',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                    {user.user_email}
                </span>
            </div>

            {/* Role badge */}
            <span style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                letterSpacing: '0.6px', textTransform: 'uppercase', flexShrink: 0,
                background: roleStyle.bg, color: roleStyle.text, border: `1px solid ${roleStyle.border}`,
            }}>
                {user.user_role}
            </span>

            {/* Edit button */}
            <button
                onClick={() => onEdit(user)}
                title="Edit user"
                style={{
                    width: 34, height: 34, borderRadius: 8, cursor: 'pointer', fontSize: 14,
                    border: '1px solid rgba(121,167,165,0.3)',
                    background: 'rgba(121,167,165,0.08)',
                    color: '#79A7A5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s', flexShrink: 0,
                }}
                onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.25)'}
                onMouseLeave={e => e.currentTarget.style.filter = 'none'}
            >
                <EditOutlined />
            </button>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

const ManageUsers = () => {
    const [roles,       setRoles]       = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [accountMeta, setAccountMeta] = useState({});

    const [editModal,     setEditModal]     = useState({ open: false, user: null });
    const [actionLoading, setActionLoading] = useState(false);

    const [addOpen,    setAddOpen]    = useState(false);
    const [addLoading, setAddLoading] = useState(false);

    const { toasts, push: toast } = useToast();

    // ── Initial load ──────────────────────────────────────────────────────────
    useEffect(() => {
        const loadUsers = async () => {
            const raw = localStorage.getItem('user_access');
            if (raw) {
                try {
                    const parsed = JSON.parse(raw);
                    const meta = {
                        account_id: parsed.license,
                        corporate:  parsed.corporate,
                        license:    parsed.license,
                    };
                    setAccountMeta(meta);
                    const result = await api_client.getallroles(parsed.license);
                    setRoles(result || []);
                } catch (e) {
                    console.error('Failed to load users', e);
                    toast('Failed to load users. Please refresh.', 'error');
                }
            }
            setLoading(false);
        };
        loadUsers();
    }, []);

    // ── Edit modal — activate / deactivate via updaterole(id, active) ─────────
    const openEdit  = (user) => setEditModal({ open: true, user });
    const closeEdit = ()     => { if (!actionLoading) setEditModal({ open: false, user: null }); };

    const handleEditConfirm = async () => {
        const { user } = editModal;
        const newActive = !user.active;
        setActionLoading(true);
        try {
            await api_client.updaterole(user.id, newActive);
            const result = await api_client.getallroles(accountMeta.license);
            setRoles(result || []);
            toast(`${user.user_name} ${user.user_surname} ${newActive ? 'activated' : 'deactivated'}.`);
            setEditModal({ open: false, user: null });
        } catch (e) {
            console.error('Update failed', e);
            toast('Failed to update user. Please try again.', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    // ── Add user ──────────────────────────────────────────────────────────────
    const handleAddUser = async (form) => {
        setAddLoading(true);
        try {
            const payload = {
                user_role:    'ADMIN',
                user_name:    form.user_name.trim(),
                user_surname: form.user_surname.trim(),
                user_email:   form.user_email.trim().toLowerCase(),
                password:     form.password,
                account_id:   accountMeta.account_id,
                corporate:    accountMeta.corporate,
                license:      accountMeta.license,
                active:       true,
            };

            await api_client.addrole(payload);
            await api_client.set_auth_register(
                form.user_email.trim().toLowerCase(),
                form.password,
            );

            const result = await api_client.getallroles(accountMeta.license);
            setRoles(result || []);
            setAddOpen(false);
            toast(`${payload.user_name} ${payload.user_surname} added successfully.`);
        } catch (e) {
            console.error('Add user failed', e);
            const msg = e?.response?.data?.error || 'Failed to add user. Please try again.';
            toast(msg, 'error');
        } finally {
            setAddLoading(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div style={{ padding: '32px 28px', maxWidth: 860 }}>
            <style>{`
                @keyframes fadeSlideIn {
                    from { opacity: 0; transform: translateY(8px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>

            {/* Header */}
            <div style={{
                display: 'flex', alignItems: 'flex-start',
                justifyContent: 'space-between', marginBottom: 28,
            }}>
                <div>
                    <h2 style={{ color: 'var(--text-primary)', fontSize: 20, fontWeight: 700, margin: '0 0 4px' }}>
                        Manage Users
                    </h2>
                    {!loading && (
                        <Text style={{ color: 'var(--text-secondary, #94A3B8)', fontSize: 13 }}>
                            {roles.length} {roles.length === 1 ? 'user' : 'users'} in this account
                        </Text>
                    )}
                </div>

                <button
                    onClick={() => setAddOpen(true)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 7,
                        padding: '9px 16px', borderRadius: 9, border: 'none',
                        background: '#79A7A5', color: '#0D0F14',
                        fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        transition: 'opacity 0.15s', flexShrink: 0,
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                    <UserAddOutlined style={{ fontSize: 15 }} />
                    Add User
                </button>
            </div>

            {/* Content */}
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
                    <Spin size="large" />
                </div>
            ) : roles.length === 0 ? (
                <div style={{
                    textAlign: 'center', padding: '60px 0',
                    color: 'var(--text-secondary, #94A3B8)', fontSize: 14,
                }}>
                    No users found.
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {roles.map((user, index) => (
                        <UserCard
                            key={user.id}
                            user={user}
                            index={index}
                            onEdit={openEdit}
                        />
                    ))}
                </div>
            )}

            {/* Add user modal */}
            <AddUserModal
                open={addOpen}
                loading={addLoading}
                onConfirm={handleAddUser}
                onCancel={() => { if (!addLoading) setAddOpen(false); }}
            />

            {/* Edit modal */}
            <EditModal
                open={editModal.open}
                user={editModal.user}
                loading={actionLoading}
                onConfirm={handleEditConfirm}
                onCancel={closeEdit}
            />

            {/* Toasts */}
            <Toast toasts={toasts} />
        </div>
    );
};

export default ManageUsers;